// Popover della toolbar tabella (round 3): menu visibilità colonne + legenda
// delle fonti dati. Pattern leggero: bottone + pannello assoluto + backdrop.
import { useState, type ReactNode } from "react";
import type { ColumnDef } from "./columns";

function Popover({
  label,
  title,
  children,
}: {
  label: string;
  title: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={title}
        className="inline-flex h-[34px] items-center gap-1.5 whitespace-nowrap rounded-full border border-line-2 bg-card px-3.5 text-[12.5px] font-semibold text-ink transition-colors hover:border-ink-4 disabled:opacity-50"
      >
        {label}
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-label="Chiudi"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 z-50 mt-1 w-64 rounded-lg border border-line bg-card p-3 text-[12px] shadow-popover">
            {children}
          </div>
        </>
      )}
    </div>
  );
}

export function ColumnsMenu({
  columns,
  hidden,
  onToggle,
  onReset,
}: {
  columns: ColumnDef[];
  hidden: Set<string>;
  onToggle: (key: string) => void;
  onReset: () => void;
}) {
  return (
    <Popover label="⚏ Colonne" title="Mostra/nascondi colonne, ripristina il layout">
      <div className="max-h-72 overflow-y-auto">
        {columns.map((c) => (
          <label key={c.key} className="flex items-center gap-2 py-0.5 text-ink-2">
            <input type="checkbox" checked={!hidden.has(c.key)} onChange={() => onToggle(c.key)} />
            {c.label}
          </label>
        ))}
      </div>
      <button
        type="button"
        onClick={onReset}
        className="mt-2 w-full rounded-md border border-line bg-paper-2 px-2 py-1 text-[11.5px] text-ink-2 hover:brightness-95"
      >
        Ripristina layout (ordine, larghezze, visibilità)
      </button>
      <p className="mt-1.5 text-[10.5px] text-ink-2">
        Trascina un'intestazione per spostare la colonna, trascina il bordo destro per
        ridimensionarla. Tutto viene ricordato.
      </p>
    </Popover>
  );
}

const LEGEND: Array<{ badge: string; cls: string; text: string }> = [
  {
    badge: "✓ API",
    cls: "text-emerald-700",
    text: "Dati cliente presi DIRETTAMENTE dall'API del marketplace (Mirakl, Metro, WooCommerce, BricoBravo, ManoMano): la fonte più affidabile.",
  },
  {
    badge: "~ Poleepo",
    cls: "text-orange-600",
    text: "Billing PROVVISORIO dal payload Poleepo: l'arricchimento dall'API del marketplace non è ancora passato (max ~15 min o tasto Sincronizza). Ogni canale ha un'API diretta, quindi questo stato è sempre temporaneo.",
  },
  {
    badge: "· in attesa",
    cls: "text-zinc-400",
    text: "Billing non ancora recuperato da nessuna fonte.",
  },
  {
    badge: "anagrafica ✓",
    cls: "text-emerald-700",
    text: "P.IVA verificata su fonti ufficiali (VIES + visurissima): ragione sociale, SDI e PEC nelle colonne vengono da lì. Unica eccezione BricoBravo, che espone anche l'SDI/PEC dichiarato dal cliente al checkout.",
  },
  {
    badge: "✓ Eureka €…",
    cls: "text-emerald-700",
    text: "Documento riscontrato nel registro Eureka: numero, data e totale coincidono (±1 cent).",
  },
  {
    badge: "✗ Eureka",
    cls: "text-rose-700",
    text: "Divergenza col registro Eureka su totale o data: da controllare.",
  },
  {
    badge: "? non in Eureka",
    cls: "text-orange-700",
    text: "Numero documento NON trovato nella serie del registro (dentro il periodo sincronizzato).",
  },
  {
    badge: "⚠ Ngg · verificato",
    cls: "text-ink",
    text: "Riga ROSSA (dal 12° giorno) o AMBRA (dal 10°): il documento è stato emesso ma non risulta ancora trasmesso allo SdI, e il termine è di 12 giorni dalla data documento (art. 21 c.4 DPR 633/72). «verificato» = è una FA e in Eureka manca il documento FAD che ne proverebbe la trasmissione. Finché la riga è ambra basta ridatare il documento; dopo i 12 giorni serve anche rinumerarlo in coda alla serie, perché numero e data devono crescere insieme.",
  },
  {
    badge: "⚠ Ngg · dichiarato",
    cls: "text-ink",
    text: "Come sopra, ma su OSS/RC/RCO: per queste sigle Eureka NON espone nessun segnale di trasmissione, quindi è lo stato dichiarato nel portale, non un riscontro. Vuol dire «non risulta trasmessa», non «non è stata trasmessa» — e vale l'opposto: una riga senza alert può comunque essere ferma, se qualcuno l'ha segnata come fatturata senza inviarla.",
  },
  {
    badge: "dedotto",
    cls: "text-ink-2",
    text: "Il numero non è stato letto da un documento emesso dal portale: è stato RICOSTRUITO dal registro Eureka agganciando data, importo e intestatario. Il documento esiste; l'abbinamento a questo ordine è dedotto, non certificato. Dove l'aggancio non era univoco la casella è stata lasciata VUOTA di proposito.",
  },
  {
    badge: "Motivo (colonna)",
    cls: "text-rose-700",
    text: "Perché la riga è ferma. Clicca l'etichetta per leggere cosa significa e cosa fare: rosso = così non si può emettere, arancione = serve una conferma.",
  },
];

/** L'elenco della legenda, da solo: sul telefono sta in un foglio, non in un popover. */
export function LegendaElenco({ grande = false }: { grande?: boolean }) {
  return (
    <div className={`flex flex-col ${grande ? "gap-3" : "gap-2"}`}>
      {LEGEND.map((l, i) => (
        <div key={i}>
          <span className={`font-semibold ${grande ? "text-[14px]" : "text-[11px]"} ${l.cls}`}>
            {l.badge}
          </span>
          <p
            className={
              grande
                ? "text-[14px] leading-relaxed text-ink"
                : "text-[11px] leading-snug text-ink-2"
            }
          >
            {l.text}
          </p>
        </div>
      ))}
    </div>
  );
}

export function LegendPopover() {
  return (
    <Popover label="ℹ Legenda" title="Cosa significano le diciture in tabella">
      <LegendaElenco />
    </Popover>
  );
}
